import { onUnmounted, ref, watch, type Ref } from 'vue';

export interface TargetRect {
	top: number;
	left: number;
	width: number;
	height: number;
}

const POLL_MS = 250;
const PADDING = 8;

/**
 * Acompanha a posição do elemento `[data-tour="..."]` na tela.
 *
 * Precisa observar de três formas porque o alvo se move por três motivos diferentes:
 * o elemento pode ainda não existir (a tela está carregando dados), pode mudar de
 * tamanho (a tabela recebeu linhas) e pode mudar de lugar (a pessoa rolou a página).
 *
 * O poll existe para o primeiro caso e é o mais chato de evitar: `MutationObserver`
 * no documento inteiro custa mais caro do que um `querySelector` a cada 250ms, e o
 * alvo costuma aparecer em menos de um segundo.
 */
export function useTargetRect(targetId: Ref<string | undefined>) {
	const rect = ref<TargetRect | null>(null);
	const element = ref<HTMLElement | null>(null);

	let poll: number | undefined;
	let resizeObserver: ResizeObserver | undefined;

	function measure() {
		const el = element.value;

		if (!el || !el.isConnected) {
			rect.value = null;

			return;
		}

		const box = el.getBoundingClientRect();

		// Elemento existe mas está oculto (aba fechada, v-show): não vale destacar.
		if (box.width === 0 && box.height === 0) {
			rect.value = null;

			return;
		}

		rect.value = {
			top: box.top - PADDING,
			left: box.left - PADDING,
			width: box.width + PADDING * 2,
			height: box.height + PADDING * 2,
		};
	}

	function attach() {
		const id = targetId.value;

		resizeObserver?.disconnect();
		resizeObserver = undefined;

		if (!id) {
			element.value = null;
			rect.value = null;

			return;
		}

		const found = document.querySelector<HTMLElement>(`[data-tour="${id}"]`);
		element.value = found;

		if (found && typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(measure);
			resizeObserver.observe(found);
		}

		measure();

		// Traz o alvo para a área visível, senão o balão aponta para fora da tela.
		found?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
	}

	function onViewportChange() {
		measure();
	}

	watch(targetId, attach, { immediate: true });

	poll = window.setInterval(() => {
		// Reanexa se o alvo ainda não existia ou se a tela o recriou.
		if (!element.value || !element.value.isConnected) attach();
		else measure();
	}, POLL_MS);

	window.addEventListener('scroll', onViewportChange, true);
	window.addEventListener('resize', onViewportChange);

	onUnmounted(() => {
		if (poll) window.clearInterval(poll);
		resizeObserver?.disconnect();
		window.removeEventListener('scroll', onViewportChange, true);
		window.removeEventListener('resize', onViewportChange);
	});

	return { rect, element };
}
